/**
 * A form field that picks one value from a searchable list (projects, catalog part numbers).
 * Looks like an input; opens a popover with a search box and a keyboard-friendly list (↑/↓, Enter, Esc).
 *  - `options` [{ value, label, sub }] filtered on the client, or `loadOptions(query)` → Promise<options>
 *    for server-side search (debounced; failures fall back to typing the value).
 *  - `allowCustom` adds "Use “XYZ”" so a value that is not in the list can still be entered.
 *  - `trigger` (an element) replaces the input-like button, e.g. a "New invoice" button that picks a project.
 */
import { forwardRef, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Loader2, Search } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

const norm = (value) => String(value ?? '').trim().toLowerCase();

export const SearchSelect = forwardRef(function SearchSelect({
  id,
  value,
  valueLabel,
  onChange,
  options,
  loadOptions,
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  emptyText = 'Nothing found',
  allowCustom = false,
  customLabel = (query) => `Use “${query}”`,
  disabled = false,
  invalid = false,
  className,
  popoverClassName,
  mono = false,
  trigger,
  align = 'start',
  testId,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
}, ref) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [remote, setRemote] = useState({ items: [], loading: false, failed: false });
  const [active, setActive] = useState(0);
  const listId = useId();
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const requestRef = useRef(0);

  // Server-side search (debounced) while the popover is open.
  useEffect(() => {
    if (!open || !loadOptions) return undefined;
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    setRemote((prev) => ({ ...prev, loading: true }));
    const timer = setTimeout(async () => {
      try {
        const items = await loadOptions(query.trim());
        if (requestRef.current === requestId) setRemote({ items: Array.isArray(items) ? items : [], loading: false, failed: false });
      } catch {
        if (requestRef.current === requestId) setRemote({ items: [], loading: false, failed: true });
      }
    }, 200);
    return () => clearTimeout(timer);
  }, [open, query, loadOptions]);

  const filtered = useMemo(() => {
    if (loadOptions) return remote.items;
    const needle = norm(query);
    const list = Array.isArray(options) ? options : [];
    if (!needle) return list.slice(0, 200);
    return list.filter((option) => norm(option.label).includes(needle) || norm(option.sub).includes(needle) || norm(option.value).includes(needle)).slice(0, 200);
  }, [options, loadOptions, remote.items, query]);

  const trimmed = query.trim();
  const showCustom = allowCustom && trimmed && !filtered.some((option) => norm(option.value) === norm(trimmed));
  const items = useMemo(
    () => (showCustom ? [...filtered, { value: trimmed, label: customLabel(trimmed), custom: true }] : filtered),
    [filtered, showCustom, trimmed, customLabel]
  );

  useEffect(() => { setActive(0); }, [query, open]);
  useEffect(() => {
    listRef.current?.querySelector?.(`[data-index="${active}"]`)?.scrollIntoView?.({ block: 'nearest' });
  }, [active]);

  const pick = (option) => {
    onChange?.(option.value, option);
    setOpen(false);
    setQuery('');
  };

  const onKeyDown = (event) => {
    if (event.key === 'ArrowDown') { event.preventDefault(); setActive((index) => Math.min(items.length - 1, index + 1)); }
    else if (event.key === 'ArrowUp') { event.preventDefault(); setActive((index) => Math.max(0, index - 1)); }
    else if (event.key === 'Enter') {
      event.preventDefault();
      if (items[active]) pick(items[active]);
    } else if (event.key === 'Escape') {
      event.stopPropagation();
      setOpen(false);
    }
  };

  const selected = (Array.isArray(options) ? options : remote.items).find((option) => option.value === value);
  const shown = valueLabel ?? selected?.label ?? value;

  return (
    <Popover open={open} onOpenChange={(next) => { setOpen(next); if (!next) setQuery(''); }}>
      <PopoverTrigger asChild>
        {trigger || (
        <button
          ref={ref}
          id={id}
          type="button"
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={ariaLabel}
          aria-describedby={ariaDescribedBy}
          aria-invalid={invalid || undefined}
          data-testid={testId}
          className={cn(
            'flex h-9 w-full min-w-0 items-center gap-2 rounded-control border bg-surface px-3 text-left text-[13.5px] text-ink transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60',
            invalid ? 'border-[color:var(--status-red)]' : 'border-line hover:border-[color:var(--ink-4)]',
            className
          )}
        >
          <span className={cn('min-w-0 flex-1 truncate', !shown && 'text-ink-4', mono && shown && 'font-mono text-[13px]')}>
            {shown || placeholder}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 text-ink-3" aria-hidden="true" />
        </button>
        )}
      </PopoverTrigger>
      <PopoverContent
        align={align}
        className={cn('w-[var(--radix-popover-trigger-width)] min-w-[260px] rounded-xl border-line bg-surface p-0 text-ink shadow-overlay', popoverClassName)}
        onOpenAutoFocus={(event) => { event.preventDefault(); inputRef.current?.focus(); }}
      >
        <div className="flex items-center gap-2 border-b border-line-2 px-3">
          <Search className="h-4 w-4 shrink-0 text-ink-3" aria-hidden="true" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={onKeyDown}
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
            aria-controls={listId}
            aria-activedescendant={items[active] ? `${listId}-${active}` : undefined}
            className="h-10 w-full min-w-0 bg-transparent text-[13.5px] text-ink outline-none placeholder:text-ink-4"
          />
          {remote.loading ? <Loader2 className="h-4 w-4 shrink-0 animate-spin text-ink-3" aria-hidden="true" /> : null}
        </div>
        <ul ref={listRef} id={listId} role="listbox" className="max-h-[260px] overflow-y-auto p-1">
          {items.map((option, index) => (
            <li
              key={`${option.custom ? 'custom-' : ''}${option.value}`}
              id={`${listId}-${index}`}
              role="option"
              aria-selected={option.value === value}
              data-index={index}
              onMouseEnter={() => setActive(index)}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => pick(option)}
              className={cn(
                'flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-2 text-[13px]',
                index === active ? 'bg-surface-hover' : '',
                option.custom && 'border-t border-line-2 text-brand-ink'
              )}
            >
              <span className="min-w-0 flex-1">
                <span className={cn('block truncate', mono && !option.custom ? 'font-mono text-[12.5px] font-semibold text-ink' : 'text-ink', option.custom && 'font-semibold text-brand-ink')}>
                  {option.label}
                </span>
                {option.sub ? <span className="block truncate text-[12px] text-ink-3">{option.sub}</span> : null}
              </span>
              {option.value === value && !option.custom ? <Check className="h-4 w-4 shrink-0 text-brand-ink" aria-hidden="true" /> : null}
            </li>
          ))}
          {!items.length ? (
            <li className="px-3 py-5 text-center text-[12.5px] text-ink-3">
              {remote.loading ? 'Searching…' : remote.failed ? 'Could not search — type the value to use it.' : emptyText}
            </li>
          ) : null}
        </ul>
      </PopoverContent>
    </Popover>
  );
});
